import {
  CSSProperties,
  useState,
  useRef,
  MouseEventHandler,
  forwardRef,
  useEffect,
  Ref,
} from 'react';
import classNames from 'classnames';
import { useClickAnyWhere } from 'usehooks-ts';
import { Link } from 'react-router-dom';

import { useClientRect } from '@/lib/hooks/use-client-rect';
import { useKeyPress } from '@/lib/hooks/use-key-press';

import { CheckboxWithLabel, MixedCheckboxWithLabel } from '../form/checkbox';
import { Overlay } from '../overlay';
import { RadioButton } from '../form/radio-button';
import { ColorPicker } from '../form/color-input';

import { Tooltip } from '../tooltip';

import styles, { dropdownTriggerGap } from './dropdown.module.scss';

export const DropdownIconSizePx = 14;

const useDropdownKeyPress = (key: string, handler: () => void, isOpen: boolean) => {
  useKeyPress(
    key,
    (e: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      handler();
    },
    { capture: true, includeInputs: true },
  );
};

export interface DropdownMenuItemActionable {
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick?: MouseEventHandler;
  onMouseOver?: MouseEventHandler;
  onMouseDown?: MouseEventHandler;
  className?: classNames.Argument;
  color?: string;
  focused?: boolean;
  tooltip?: string;
}

export interface DropdownMenuItemButton extends DropdownMenuItemActionable {
  type?: 'button';
}

interface DropdownMenuItemLink extends DropdownMenuItemActionable {
  type: 'link';
  href: string;
  external?: boolean;
}

export interface DropdownMenuItemCheckbox extends DropdownMenuItemActionable {
  type: 'checkbox';
  checked?: boolean;
  onChange: (isChecked: boolean) => void;
}
export interface DropdownMenuItemMixedCheckbox extends DropdownMenuItemActionable {
  type: 'mixed-checkbox';
  onClick: () => void;
}

interface DropdownMenuItemRadio extends DropdownMenuItemActionable {
  type: 'radio';
  checked?: boolean;
}

interface DropdownMenuItemColor extends DropdownMenuItemActionable {
  type: 'color';
  label: string;
  color: string;
  presetColors?: string[];
  onChange: (color: string | undefined) => void;
}

export interface DropdownMenuItemDivider {
  type: 'divider';
}

export interface DropdownMenuItemHeader {
  type: 'header';
  label: string;
}

export type DropdownMenuItem =
  | DropdownMenuItemButton
  | DropdownMenuItemLink
  | DropdownMenuItemCheckbox
  | DropdownMenuItemMixedCheckbox
  | DropdownMenuItemRadio
  | DropdownMenuItemDivider
  | DropdownMenuItemHeader
  | DropdownMenuItemColor;

type DropdownProps = {
  items: DropdownMenuItem[];
  trigger: (isOpen: boolean, setIsOpen: (isOpen: boolean) => void) => React.ReactNode;
  align: 'left' | 'right' | 'both';
  animated?: boolean;
  className?: classNames.Argument;
  menuClassName?: classNames.Argument;
  style?: CSSProperties;
};

export const Dropdown = (props: DropdownProps) => {
  const { items, trigger, align, className, style, animated = true } = props;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const triggerRect = useClientRect(triggerRef, [isOpen]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownRect = useClientRect(dropdownRef, [isOpen]);

  useClickAnyWhere((e) => {
    const inTrigger = triggerRef.current?.contains(e.target as Node) ?? false;
    const inDropdown = dropdownRef.current?.contains(e.target as Node) ?? false;

    if (!isOpen || inTrigger || inDropdown) {
      return;
    }
    setIsOpen(false);
  });

  useKeyPress(
    'Escape',
    (e: KeyboardEvent) => {
      if (isOpen) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
      }
    },
    { capture: true, includeInputs: true },
  );

  const handleMenuClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  const shouldOpenAbove = () =>
    getLowerAnchorY() + getDropdownHeight() > window.innerHeight &&
    getUpperAnchorY() - getDropdownHeight() > 0;

  const getLowerAnchorY = () => triggerRect?.bottom ?? 0;
  const getUpperAnchorY = () => triggerRect?.top ?? 0;
  const getAnchorY = () => (shouldOpenAbove() ? getUpperAnchorY() : getLowerAnchorY());

  const getDropdownHeight = () => dropdownRect?.height ?? 0;
  const getViewportHeight = () => window.innerHeight - parseInt(dropdownTriggerGap) * 2;

  // When positioning below the trigger, we shift the dropdown up if it would overflow the viewport
  const getYOverflow = () => Math.max(0, getAnchorY() + getDropdownHeight() - getViewportHeight());

  const getUpperY = () => getUpperAnchorY() - getDropdownHeight();
  const getLowerY = () => Math.max(0, getAnchorY() - getYOverflow());

  const dropdownPos =
    triggerRect === null || dropdownRect === null
      ? undefined
      : {
          top: shouldOpenAbove() ? getUpperY() : getLowerY(),
          left: align === 'right' ? triggerRect.right - dropdownRect.width : triggerRect.left,
          minWidth: align === 'both' ? triggerRect.width : 'auto',
        };

  return (
    <div
      className={classNames(styles.dropdown, className, {
        [styles.dropdownOpen]: isOpen,
        [styles.openAbove]: shouldOpenAbove(),
      })}
      style={style}>
      <div ref={triggerRef} className={styles.triggerContainer}>
        {trigger(isOpen, setIsOpen)}
      </div>

      {isOpen && (
        <Overlay>
          <DropdownMenu
            ref={dropdownRef}
            items={items}
            animated={animated}
            className={props.menuClassName}
            style={dropdownPos}
            closeDropdown={handleMenuClick}
            isOpen={isOpen}
          />
        </Overlay>
      )}
    </div>
  );
};

export const DropdownMenu = forwardRef(function DropdownMenu(
  {
    items: itemsProp,
    className,
    style,
    animated,
    closeDropdown,
    isOpen = true,
  }: {
    items: DropdownMenuItem[];
    className?: classNames.Argument;
    style?: CSSProperties;
    animated?: boolean;
    closeDropdown?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    isOpen?: boolean;
  },
  ref?: Ref<HTMLDivElement>,
) {
  const [keyboardFocusedIndex, setKeyboardFocusedIndex] = useState<number>(-1);
  const focusedItemRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);

  const isItemFocusable = (item: DropdownMenuItem): boolean => {
    if (item.type === 'divider' || item.type === 'header') {
      return false;
    }
    if ('disabled' in item && item.disabled === true) {
      return false;
    }
    return true;
  };

  const getNextFocusableIndex = (currentIndex: number): number => {
    const afterCurrent = itemsProp.slice(currentIndex + 1).findIndex(isItemFocusable);
    if (afterCurrent !== -1) {
      return currentIndex + 1 + afterCurrent;
    }

    const fromStart = itemsProp.slice(0, currentIndex + 1).findIndex(isItemFocusable);
    return fromStart !== -1 ? fromStart : currentIndex;
  };

  const getPrevFocusableIndex = (currentIndex: number): number => {
    const beforeCurrent = itemsProp.slice(0, currentIndex).findLastIndex(isItemFocusable);
    if (beforeCurrent !== -1) {
      return beforeCurrent;
    }

    const fromEnd = itemsProp.slice(currentIndex).findLastIndex(isItemFocusable);
    return fromEnd !== -1 ? currentIndex + fromEnd : currentIndex;
  };

  const getFirstFocusableIndex = () => itemsProp.findIndex(isItemFocusable);
  const getLastFocusableIndex = () => itemsProp.findLastIndex(isItemFocusable);

  useDropdownKeyPress(
    'ArrowDown',
    () => {
      setKeyboardFocusedIndex((prev) =>
        prev < 0 ? getFirstFocusableIndex() : getNextFocusableIndex(prev),
      );
    },
    isOpen,
  );

  useDropdownKeyPress(
    'ArrowUp',
    () => {
      setKeyboardFocusedIndex((prev) =>
        prev < 0 ? getLastFocusableIndex() : getPrevFocusableIndex(prev),
      );
    },
    isOpen,
  );

  useDropdownKeyPress('Enter', () => focusedItemRef.current?.click(), isOpen);
  useDropdownKeyPress(' ', () => focusedItemRef.current?.click(), isOpen);

  useEffect(() => {
    if (!isOpen) {
      setKeyboardFocusedIndex(-1);
    }
  }, [isOpen]);

  const items = itemsProp.map((item, idx) => {
    if (item.type === 'divider' || item.type === 'header') {
      return item;
    }

    const existingFocused = 'focused' in item && item.focused === true;
    const isFocused = existingFocused || idx === keyboardFocusedIndex;

    return {
      ...item,
      focused: isFocused,
    };
  });

  const focusedItemIndex = items.findIndex((item) => 'focused' in item && item.focused);

  useEffect(() => {
    focusedItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [focusedItemIndex]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Clear focus when mouse moves over empty space (not over any item)
    if (!(e.target as HTMLElement).closest(`.${styles.item}`)) {
      setKeyboardFocusedIndex(-1);
    }
  };

  return (
    <div
      ref={ref}
      className={classNames(styles.dropdownMenu, { [styles.animated]: animated }, className)}
      style={style}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setKeyboardFocusedIndex(-1)}>
      {items.map((item, idx) => {
        const isFocused = 'focused' in item && item.focused;
        if ('tooltip' in item) {
          return (
            <Tooltip key={idx} content={item.tooltip} placement="right">
              <div>
                <DropdownMenuItem
                  item={item}
                  itemIndex={idx}
                  ref={isFocused ? focusedItemRef : null}
                  closeDropdown={closeDropdown}
                  setFocusedIndex={setKeyboardFocusedIndex}
                />
              </div>
            </Tooltip>
          );
        }

        return (
          <DropdownMenuItem
            key={idx}
            item={item}
            itemIndex={idx}
            ref={isFocused ? focusedItemRef : null}
            closeDropdown={closeDropdown}
            setFocusedIndex={setKeyboardFocusedIndex}
          />
        );
      })}
    </div>
  );
});

const DropdownMenuItem = forwardRef(function DropdownMenuItem(
  {
    item,
    itemIndex,
    closeDropdown,
    setFocusedIndex,
  }: {
    item: DropdownMenuItem;
    itemIndex: number;
    closeDropdown?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    setFocusedIndex?: (index: number) => void;
  },
  ref: React.Ref<HTMLButtonElement | HTMLAnchorElement | HTMLDivElement>,
) {
  const colorPickerRef = useRef<HTMLDivElement>(null);

  if (item.type === 'divider') {
    return <hr className={styles.divider} />;
  }

  if (item.type === 'header') {
    return <p className={styles.header}>{item.label}</p>;
  }

  const sharedProps = {
    className: classNames(styles.item, item.className, {
      [styles.disabled]: item.disabled,
      [styles.focused]: item.focused === true,
    }),
    style: { color: item.color },
    onClick: (e: React.MouseEvent) => {
      item.onClick?.(e);
      if (item.disabled !== true) {
        closeDropdown?.(e as React.MouseEvent<HTMLDivElement, MouseEvent>);
      }
    },
    onMouseMove: () => {
      if (item.disabled !== true) {
        setFocusedIndex?.(itemIndex);
      } else {
        setFocusedIndex?.(-1);
      }
    },
    onMouseOver: item.onMouseOver,
    onMouseDown: item.onMouseDown,
    tabIndex: -1,
  };

  switch (item.type) {
    case 'link':
      return (
        <Link
          {...sharedProps}
          to={item.href}
          {...(item.external === true && { target: '_blank', rel: 'noopener noreferrer' })}
          ref={ref as React.Ref<HTMLAnchorElement>}>
          {item.icon !== undefined && item.icon}
          {item.label}
        </Link>
      );
    case 'checkbox':
      return (
        <CheckboxWithLabel
          {...sharedProps}
          ref={ref as React.Ref<HTMLLabelElement>}
          checked={item.checked}
          onChange={({ checked }) => item.onChange?.(checked)}
          onClick={(e) => e.stopPropagation()}>
          {item.icon !== undefined && item.icon}
          {item.label}
        </CheckboxWithLabel>
      );
    case 'mixed-checkbox':
      return (
        <MixedCheckboxWithLabel
          {...sharedProps}
          ref={ref as React.Ref<HTMLLabelElement>}
          onClick={(e) => {
            e.stopPropagation();
            item.onClick?.();
          }}>
          {item.label}
        </MixedCheckboxWithLabel>
      );
    case 'radio':
      return (
        <button
          {...sharedProps}
          type="button"
          ref={ref as React.Ref<HTMLButtonElement>}
          onClick={(e) => {
            e.stopPropagation();
            item.onClick?.(e);
            if (item.disabled !== true) {
              closeDropdown?.(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>);
            }
          }}>
          <RadioButton
            name={typeof item.label === 'string' ? item.label : ''}
            label={item.label}
            isChecked={item.checked}
          />
        </button>
      );
    case 'color':
      return (
        <div
          className={classNames(styles.item, styles.colorPickerItem, {
            [styles.focused]: item.focused === true,
          })}
          onMouseMove={sharedProps.onMouseMove}
          ref={(el) => {
            if (el) {
              el.click = () => colorPickerRef.current?.click();
            }
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
          }}
          onClick={(e) => e.stopPropagation()}>
          <ColorPicker
            ref={colorPickerRef}
            color={item.color}
            label={item.label}
            size="small"
            className={styles.colorPicker}
            presetColors={item.presetColors}
            onChange={item.onChange}
          />
        </div>
      );
    default:
      return (
        <button
          {...sharedProps}
          type="button"
          disabled={item.disabled}
          ref={ref as React.Ref<HTMLButtonElement>}>
          {item.icon !== undefined && item.icon}
          {item.label}
        </button>
      );
  }
});
